import { clientLogger } from '@/lib/clientLogger';
/**
 * Device Token Service
 * 
 * Manages device tokens for background location tracking.
 * Device tokens are long-lived and used by the native HTTP layer
 * to authenticate location updates without JS execution.
 */

import { Preferences } from '@capacitor/preferences';
import { ENV } from '@/config/environment';
import { authService } from './firebase';
import { isNative } from './capacitor';
import { apiService } from './api';

const DEVICE_ID_KEY = 'presence_device_id';
const DEVICE_TOKEN_KEY = 'presence_device_token';

// Mutex to prevent concurrent token requests
let tokenRequestInFlight: Promise<string> | null = null;

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Get or create a persistent device ID.
 *
 * TODO(secure-storage): @capacitor/preferences is NOT Keystore/Keychain — it is
 * SharedPreferences on Android and UserDefaults on iOS. Both are unencrypted and both are
 * included in device backups, so the long-lived device token stored below (used as
 * `Authorization: Presence {token}` for background location POSTs made natively, without JS)
 * sits in plaintext. Low severity: reading it needs physical access to an unlocked device or
 * root/jailbreak, and its scope is "post locations for this device", not account access.
 * To harden: move DEVICE_TOKEN_KEY to a Keychain/EncryptedSharedPreferences-backed plugin —
 * and migrate (read old -> write new -> delete old), or every existing device re-registers.
 */
export async function getOrCreateDeviceId(): Promise<string> {
    const { value } = await Preferences.get({ key: DEVICE_ID_KEY });

    if (value) {
        clientLogger.info('[DeviceToken] Using existing device ID:', value.substring(0, 8) + '...');
        return value;
    }

    const newId = generateUUID();
    await Preferences.set({ key: DEVICE_ID_KEY, value: newId });
    clientLogger.info('[DeviceToken] Created new device ID:', newId.substring(0, 8) + '...');
    return newId;
}

/**
 * Get the stored device token (if any)
 */
export async function getStoredDeviceToken(): Promise<string | null> {
    const { value } = await Preferences.get({ key: DEVICE_TOKEN_KEY });
    return value;
}

/**
 * Store the device token
 */
async function storeDeviceToken(token: string): Promise<void> {
    await Preferences.set({ key: DEVICE_TOKEN_KEY, value: token });
}

/**
 * Clear the stored device token
 */
export async function clearDeviceToken(): Promise<void> {
    await Preferences.remove({ key: DEVICE_TOKEN_KEY });
    clientLogger.info('[DeviceToken] Cleared device token');
}

/**
 * Internal function to request a new device token from the backend.
 */
async function doRequestDeviceToken(): Promise<string> {
    const deviceId = await getOrCreateDeviceId();

    // Get Firebase token for authentication
    const firebaseToken = await authService.getCurrentToken();
    if (!firebaseToken) {
        throw new Error('Not authenticated - cannot request device token');
    }

    clientLogger.info('[DeviceToken] Requesting new device token for device:', deviceId.substring(0, 8) + '...');

    const orgId = apiService.getOrgId();
    const response = await fetch(`${ENV.API.BASE_URL}/location/device-token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${firebaseToken}`,
            ...(orgId ? { 'x-org-id': orgId } : {}),
        },
        body: JSON.stringify({ "device_id": deviceId }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        clientLogger.debug('[DeviceToken] Failed to get device token:', response.status, errorText);
        throw new Error(`Failed to get device token: ${response.status}`);
    }

    const data = await response.json();
    const token = data.device_token || data.deviceToken;

    if (!token) {
        throw new Error('Invalid response: missing device_token');
    }

    await storeDeviceToken(token);
    clientLogger.info('[DeviceToken] Device token obtained and stored');

    return token;
}

/**
 * Request a new device token from the backend.
 * Uses mutex to prevent concurrent requests which would revoke valid tokens.
 */
export async function requestDeviceToken(): Promise<string> {
    if (!isNative()) {
        throw new Error('Device tokens are only available on native platforms');
    }

    // If a request is already in flight, wait for it instead of making a new one
    if (tokenRequestInFlight) {
        clientLogger.info('[DeviceToken] Request already in flight, waiting...');
        return tokenRequestInFlight;
    }

    // Start a new request and store the promise
    tokenRequestInFlight = doRequestDeviceToken().finally(() => {
        // Clear the in-flight promise when done (success or error)
        tokenRequestInFlight = null;
    });

    return tokenRequestInFlight;
}

/**
 * Ensure we have a valid device token.
 * Uses mutex to prevent concurrent requests.
 */
export async function ensureDeviceToken(): Promise<string> {
    return await requestDeviceToken();
}

/**
 * Initialize device token on login/startup.
 * Should be called after successful authentication.
 */
export async function initializeDeviceToken(): Promise<string | null> {
    if (!isNative()) {
        clientLogger.info('[DeviceToken] Skipping - not on native platform');
        return null;
    }

    try {
        // Reuse the stored token if we have one. The server uses a 30-day sliding
        // window that renews the token on every location update, so a token in
        // active use stays valid indefinitely. Requesting a fresh one on every
        // launch would revoke the previous token server-side — and if the app is
        // killed before the new header is persisted to the native config, the
        // background service keeps POSTing with a now-revoked token (silent 401s).
        const existing = await getStoredDeviceToken();
        if (existing) {
            clientLogger.info('[DeviceToken] Reusing stored device token');
            return existing;
        }

        const token = await ensureDeviceToken();
        return token;
    } catch (error) {
        clientLogger.debug('[DeviceToken] Failed to initialize:', error);
        return null;
    }
}
